import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import styled from 'styled-components'
import {Nullable} from '../../app/TypeHelpers'
import RRFTFacilitySearchComponent from './ResidentReserveFacilityTopComponents/RRFTFacilitySearchComponent'
import RRFTMyReservationsComponent from './ResidentReserveFacilityTopComponents/RRFTMyReservationsComponent'
import ReservationsRepository from './ReservationsRepository'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import CondominiumsRepository from '../condominiums/CondominiumsRepository'
import FacilitiesRepository from '../facilities/FacilitiesRepository'
import {
    getYearAfterNextDateFac,
    getYearDateFac,
    NatAsyncCallFunction,
} from '../../__NoAutomatedTests/common/util/Natutil'
import {PlainLink, SecondaryButtonStyledLink} from '../../styledComponents/Links'
import {DisplayItemDividerForPageTop, DisplayItemTitle} from '../../styledComponents/DisplayItem'
import {StyledPageTitle, ViewOnlyModeMessage} from './components/CommonStyle'
import {BlockSpacer, RenderSwitchByBreakPoint} from '../../styledComponents/ResponsiveConvenience'
import CollapseHTMLDisplay from './components/CollapseHTMLDisplay'
import {FontSizeInPixel, ResidentScrollBoundary} from '../../styledComponents/Constants'
import {Messages} from '../../Messages'
import {judgeFacilityReserveEditAuthority} from './helpers/helpers'
import {withScrollToTop} from '../shared/hoc/withScrollToTop'
import {
    clearResidentMyReservationsPageDisplayState,
} from './ResidentMyReservationsComponents/RMRReservationStatusesComponent'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {RightRowFlexContainer} from '../../styledComponents/FlexComponents'
import HolidaysRepository from '../HolidaysRepository'
import {formatForDateInput} from '../../formatters/formatDate'
import moment from 'moment'

interface Props {
    condominiumCd: string
    condominiumsRepo: CondominiumsRepository
    facilitiesRepo: FacilitiesRepository
    reservationsRepo: ReservationsRepository
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExResident
    holidaysRepo: HolidaysRepository
}

interface State {
    facilityReserveIntroductory: Nullable<string>
    viewOnlyMode: boolean
    holidays: string[]
    currentDateTime: Nullable<moment.Moment>
}

class ResidentReserveFacilityTopPage extends Component<Props & ResidentPagePropsSet, State> {

    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)

        this.state = {
            facilityReserveIntroductory: null,
            viewOnlyMode: false,
            holidays: [],
            currentDateTime: null,
        }
    }

    async getHolidays(minDate: string, maxDate: string): Promise<Nullable<string[]>> {
        try {
            return await NatAsyncCallFunction(
                this.props.holidaysRepo.getHolidays.bind(
                    this.props.holidaysRepo,
                    minDate,
                    maxDate,
                ),
                this.props.navigate,
            )
        } catch (e) {
            throw e
        }
    }

    async componentDidMount(): Promise<void> {
        let viewOnlyMode: boolean = false
        try {
            viewOnlyMode = !judgeFacilityReserveEditAuthority()
        } catch (e) {
            this.props.navigate(`/resident/error`, {state: {message: Messages.ESYCC0025.toString()}})
            return
        }

        await this.props.eventEmitter.showLoading()
        try {
            const {condominiumsRepo, condominiumCd, navigate} = this.props
            const facilityReserveIntroductory: Nullable<string> = await NatAsyncCallFunction(
                condominiumsRepo.getFacilityReserveIntroductory.bind(condominiumsRepo, condominiumCd), navigate,
            )
            if (facilityReserveIntroductory === null) {
                return
            }

            const currentTimeResponse = await NatAsyncCallFunction(
                this.props.currentDateTimeRepo.getCurrentDateTime.bind(this.props.currentDateTimeRepo),
                navigate,
            )
            if (currentTimeResponse === null || currentTimeResponse.details === null) {
                return
            }
            const currentDate = moment(currentTimeResponse.details.currentDateTime)

            const minDate: string = getYearDateFac()
            const maxDate: string = getYearAfterNextDateFac(currentDate.toLocaleString())
            const holidays: Nullable<string[]> =
                await this.getHolidays(formatForDateInput(minDate), formatForDateInput(maxDate))

            this.setState({
                facilityReserveIntroductory: facilityReserveIntroductory,
                viewOnlyMode: viewOnlyMode,
                holidays: holidays === null ? [] : holidays,
                currentDateTime: currentDate,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }

        clearResidentMyReservationsPageDisplayState()
    }

    render(): ReactNode {
        if (this.state.facilityReserveIntroductory === null) {
            return null
        }

        return <>
            <StyledPageTitle>
                施設予約
            </StyledPageTitle>
            <ContentBody>
                {
                    this.state.viewOnlyMode && <>
                        <BlockSpacer height={'20px'}/>
                        <ViewOnlyModeMessage>{Messages.IRESR0008.toString()}</ViewOnlyModeMessage>
                    </>
                }
                {
                    this.state.facilityReserveIntroductory && <>
                        <BlockSpacer height={'20px'}/>
                        <CollapseHTMLDisplay
                            showScroll={false} initialOpen={false}
                            fontSizeInPixel={FontSizeInPixel.Middle}
                            lineHeightInPixel={21}
                            contents={this.state.facilityReserveIntroductory}
                        />
                    </>
                }
                <BlockSpacer height={'20px'}/>
                <RRFTFacilitySearchComponent
                    condominiumCd={this.props.condominiumCd}
                    facilitiesRepo={this.props.facilitiesRepo}
                    reservationsRepo={this.props.reservationsRepo}
                    maxFacilitiesDisplayCount={100}
                    eventEmitter={this.props.eventEmitter}
                    navigate={this.props.navigate}
                    holidays={this.state.holidays}
                    currentDateTime={this.state.currentDateTime}
                />
            </ContentBody>
            <BlockSpacer height={'30px'}/>
            <DisplayItemDividerForPageTop/>
            <BlockSpacer height={'30px'}/>
            <DisplayItemTitle>あなたの予約</DisplayItemTitle>
            <ContentBody>
                <RRFTMyReservationsComponent
                    condominiumCd={this.props.condominiumCd}
                    reservationsRepo={this.props.reservationsRepo}
                    eventEmitter={this.props.eventEmitter}
                    navigate={this.props.navigate}
                />
                <BlockSpacer height={'20px'}/>
                <RenderSwitchByBreakPoint
                    under={
                        <MyReservationsButton
                            to={`/resident/condominiums/${this.props.condominiumCd}/my-reservations`}>
                            予約一覧へ
                        </MyReservationsButton>
                    }
                    over={
                        <RightRowFlexContainer>
                            <MyReservationsLink
                                to={`/resident/condominiums/${this.props.condominiumCd}/my-reservations`}>
                                予約一覧へ
                            </MyReservationsLink>
                        </RightRowFlexContainer>
                    }
                />
                <BlockSpacer height={'15px'}/>
            </ContentBody>
        </>
    }
}

export default createResidentPage(withScrollToTop(ResidentReserveFacilityTopPage, ResidentScrollBoundary))

const ContentBody = styled.div`
  max-width: 738px;
  margin: auto;
`

const MyReservationsButton = styled(SecondaryButtonStyledLink)`
  margin: 0 auto;
  max-width: 384px;
`

const MyReservationsLink = styled(PlainLink)`
  display: inline-block;
  margin: 0;
`
